import { useEffect } from "react";
import { App as AppContainer, ConfigProvider, Layout, ThemeConfig } from "antd";
import { observer } from "mobx-react-lite";

import dark from "@/app/styles/dark.json";
import light from "@/app/styles/light.json";
import { Aside } from "@/app/template/aside";
import { Header } from "@/app/template/header";
import { useConnectPartyAbilities } from "@/features/check-abilities";
import { ColorTheme } from "@/shared/constants/common";
import { useGeneralState, usePartiesState, useUserState } from "@/shared/stores/app-state";
import { UIModalConsent } from "@/shared/ui/ui-modal-consent";
import { UISpinner } from "@/shared/ui/ui-spinner";
import { ToasterInit } from "@/shared/ui/ui-toaster";

import { useGetPartyFromUrl } from "./hooks";
import { Routing } from "./routing";
import { StyleColorScheme } from "./style-color-scheme";
import s from "./app.module.scss";

type ThemeTokenMap = Record<ColorTheme, ThemeConfig>;

const themes: ThemeTokenMap = { light, dark };

export const App = observer(() => {
	const { theme, isMobile, init } = useGeneralState();
	const { isLoggedIn } = useUserState();
	const partiesStore = usePartiesState();

	const partyUUID = useGetPartyFromUrl();

	useConnectPartyAbilities(partyUUID);

	useEffect(() => {
		init();
	}, []);

	useEffect(() => {
		if (isLoggedIn) partiesStore.loadData();
	}, [isLoggedIn, partiesStore]);

	return (
		<ConfigProvider theme={{ cssVar: true, ...themes[theme] }}>
			<AppContainer data-testid="template" className={s.template}>
				<StyleColorScheme />

				{isLoggedIn && !partiesStore.isLoadedParties ? (
					<div className="size-full">
						<UISpinner />
					</div>
				) : (
					<>
						{isLoggedIn && !isMobile && <Aside />}

						<main data-testid="main" className={s.main}>
							<Header />

							<Layout data-testid="content" className={s.content}>
								<Routing />
							</Layout>
						</main>
					</>
				)}

				<ToasterInit theme={theme} />

				<UIModalConsent />
			</AppContainer>
		</ConfigProvider>
	);
});
